// src/pages/Login/LoginPage.jsx

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Container, CssBaseline, Box, Typography, TextField, Button, Avatar, Paper } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import "react-toastify/dist/ReactToastify.css";
import socket from "../../socket/Socket.js";

const theme = createTheme({
	palette: {
		mode: "dark",
		primary: {
			main: "#01a79e",
		},
		background: {
			default: "#121212",
			paper: "#1d1d1d",
		},
		text: {
			primary: "#ffffff",
		},
	},
});

const LoginPage = ({ setAuthenticated }) => {
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const usernameRef = useRef("");
	const passwordRef = useRef("");

	useEffect(() => {
		usernameRef.current = username;
		passwordRef.current = password;
	}, [username, password]);

	const handleLogin = async (e) => {
		if (e && e.preventDefault) {
			e.preventDefault();
		}

		try {
			const response = await axios.post("https://mitserver.app:9005/login", {
				email: username || usernameRef.current,
				password: password || passwordRef.current,
			});

			if (response.data.ok) {
				const data = response.data.data;
				const token = response.data.token;

				localStorage.setItem("user", JSON.stringify(data));
				localStorage.setItem("token", token);
				const selectedAccount = {
					account: data.demoAcc,
					userId: data.id,
					token: data.demoToken,
				};

				connectSocket(data, token);

				localStorage.setItem("selectedAccount", JSON.stringify(selectedAccount));
				setAuthenticated(true);

				localStorage.setItem("contractCategory", "multiplier");
			} else {
				console.log("LOGIN ERROR:", response.data.message);
				toast.dark(response.data.message || "Error during login", { pauseOnHover: false });
			}
		} catch (error) {
			console.error("LOGIN ERROR:", error);
		}
	};

	const connectSocket = (data, token) => {
		socket.disconnect();

		socket.io.opts.query = {
			version: "V2.1",
			userId: data.id,
			token: token,
		};

		socket.connect();
		socket.once("connect", () => {
			console.log("✅ Socket reconectado, enviando login");
		});
	};

	const handleForgotPassword = async () => {
		try {
			if (username === "") {
				toast.dark("Por favor escribe tu correo electrónico", { pauseOnHover: false });
				return;
			}
			const response = await axios.post("https://mitserver.app:9005/forgot", {
				email: username,
			});
			if (response.status === 200) {
				toast.dark(response.data.message || "Contraseña Enviada", { pauseOnHover: false });
			} else {
				toast.dark(response.data.message || "Error al enviar correo", { pauseOnHover: false });
			}
		} catch (error) {
			toast.dark(error.response?.data?.message || "Un error ha ocurrido. Por favor intenta de nuevo.", { pauseOnHover: false });
		}
	};

	useEffect(() => {
		const handleLoginFromMessage = () => {
			handleLogin();
		};

		const handleIncomingMessage = (event) => {
			try {
				let data;
				if (typeof event.data === "string") {
					if (event.data.startsWith("{") || event.data.startsWith("[")) {
						data = JSON.parse(event.data);
					} else {
						return;
					}
				} else {
					data = event.data;
				}

				if (data && data.email && data.password) {
					setUsername(data.email);
					setPassword(data.password);
					console.log("Datos recibidos:", data);
					setTimeout(handleLoginFromMessage, 100);
				}
			} catch (error) {
				console.error("Error al parsear el mensaje:", error);
			}
		};

		window.addEventListener("message", handleIncomingMessage);

		return () => {
			window.removeEventListener("message", handleIncomingMessage);
		};
	}, [handleLogin]);

	return (
		<ThemeProvider theme={theme}>
			<Box
				sx={{
					backgroundImage: 'url("/assets/bg.jpg")',
					backgroundSize: "cover",
					backgroundPosition: "center",
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexDirection: { xs: "column", md: "row" },
					padding: { xs: "20px", md: 0 },
				}}>
				<CssBaseline />
				<Container
					component="main"
					maxWidth="xs"
					sx={{
						marginLeft: { xs: "0", md: "40%" },
						width: { xs: "100%", md: "auto" },
					}}>
					<Paper
						elevation={6}
						style={{
							padding: "20px",
							borderRadius: "10px",
							background: "transparent",
						}}>
						<Box
							sx={{
								marginTop: { xs: 2, md: 8 },
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
							}}>
							<Box sx={{ m: 1, width: { xs: "70%", md: "100%" } }}>
								<img src="/assets/logoMV.png" alt="Login" width="100%" />
							</Box>
							<Typography
								component="h1"
								variant="h5"
								sx={{
									color: "#fff",
									textShadow: "0 0 10px #4599d9",
									marginTop: { xs: "30px", md: "60px" },
									fontSize: { xs: "1.5rem", md: "h5.fontSize" },
								}}>
								Inicio de Sesión
							</Typography>
							<Box component="form" onSubmit={handleLogin} sx={{ mt: 1, width: "100%" }}>
								<TextField
									margin="normal"
									required
									fullWidth
									id="username"
									label="E-mail"
									name="username"
									autoComplete="username"
									autoFocus
									value={username}
									onChange={(e) => setUsername(e.target.value)}
									InputLabelProps={{ style: { color: "#ffffff" } }}
									InputProps={{
										style: { color: "#ffffff" },
										sx: {
											"& fieldset": { borderColor: "#4599d9" },
											"&:hover fieldset": { borderColor: "#f74712" },
											"&.Mui-focused fieldset": { borderColor: "#f74712" },
										},
									}}
								/>
								<TextField
									margin="normal"
									required
									fullWidth
									name="password"
									label="Contraseña"
									type="password"
									id="password"
									autoComplete="current-password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									InputLabelProps={{ style: { color: "#ffffff" } }}
									InputProps={{
										style: { color: "#ffffff" },
										sx: {
											"& fieldset": { borderColor: "#4599d9" },
											"&:hover fieldset": { borderColor: "#f74712" },
											"&.Mui-focused fieldset": { borderColor: "#f74712" },
										},
									}}
								/>

								<Button
									type="submit"
									fullWidth
									variant="contained"
									sx={{
										mt: 3,
										mb: 2,
										backgroundColor: "#4599d9",
										color: "#fff",
										"&:hover": {
											backgroundColor: "#3d87c3",
										},
										boxShadow: "0 0 5px #4599d9, 0 0 5px #4599d9",
									}}>
									Iniciar
								</Button>
								<Typography align="center">
									<Link to="#" onClick={handleForgotPassword} style={{ color: "#f74712", textShadow: "0 0 5px#cc2e3d" }}>
										¿Olvidaste tu contraseña?
									</Link>
								</Typography>
								<Button
									fullWidth
									variant="contained"
									sx={{
										mt: 2,
										mb: 2,
										backgroundColor: "#833ab4",
										color: "#fff",
										"&:hover": {
											backgroundColor: "#833ab4",
										},
										boxShadow: "0 0 5px #833ab4, 0 0 5px #833ab4",
									}}
									onClick={() => (window.location.href = "/register")}>
									Registrarse
								</Button>
								<Button
									fullWidth
									variant="contained"
									sx={{
										mt: 2,
										backgroundColor: "#ff444f",
										color: "#fff",
										"&:hover": {
											backgroundColor: "#e63946",
										},
									}}
									onClick={() => {
									const derivUrl = process.env.REACT_APP_DERIV_SIGNUP_URL || "https://deriv.partners/rx?sidi=64C55A01-B332-4C25-BAA4-C09E078C714D&utm_campaign=dynamicworks&utm_medium=affiliate&utm_source=CU37849";
									console.log("Abriendo URL de Deriv:", derivUrl);
									window.open(derivUrl, "_blank");
								}}>
									Registrarse en Deriv
								</Button>

								<Typography
									sx={{
										mt: 3,
										fontSize: "0.75rem",
										color: "#fff",
										textAlign: "justify",
										lineHeight: 1.5,
									}}>
									Deriv ofrece derivados complejos, como opciones y contratos por diferencia (CFD). Estos productos pueden no ser
									adecuados para todos los clientes, y operar con ellos supone un riesgo. Por favor, asegúrese de comprender los
									siguientes riesgos antes de operar con productos de Deriv: a) podría perder parte o la totalidad del dinero
									invertido en la operación; b) si su operación implica conversión de divisas, los tipos de cambio afectarán sus
									ganancias y pérdidas. Nunca opere con dinero prestado o que no pueda permitirse perder.
								</Typography>
							</Box>
						</Box>
					</Paper>
				</Container>
				<Box
					sx={{
						m: 1,
						width: { xs: "70%", md: "auto" },
						display: { xs: "none", md: "block" },
					}}>
					<img src="/assets/ticksy3.png" alt="Login" width="100%" />
				</Box>
			</Box>
		</ThemeProvider>
	);
};

export default LoginPage;
